import { useEffect, useMemo, useState } from 'react';
import { useRoute } from 'react-router5';
import {
  differenceInYears,
  formatDistanceToNowStrict,
  differenceInDays,
  differenceInHours,
  differenceInSeconds,
  differenceInMinutes,
} from 'date-fns';

import { dateFnsLocale, format } from '$utils';
import { useTranslator } from '$hooks/useTranslator';
import { useCurrentLang } from '$hooks/useCurrentLang';
import { PROFILE, RESOLVE } from '$shared/constants/pages';

export function useDate(ts: number): string {
  const { route } = useRoute();
  const t = useTranslator();
  const currentLang = useCurrentLang();
  const [now, setNow] = useState(new Date());
  const date = new Date(ts * 1000);
  const updateNow = () => {
    setNow(new Date());
  };

  useEffect(() => {
    if (differenceInHours(now, date)) {
      return;
    }
    const interval = setInterval(updateNow, 15 * 1000);
    return () => clearInterval(interval);
  }, [ts]);

  return useMemo(() => {
    if (!ts) {
      return '';
    }

    const locale = dateFnsLocale[currentLang.key] ?? dateFnsLocale.en;
    const diffInDays = differenceInDays(now, date);

    if (differenceInSeconds(now, date) < 5) {
      return t('time_now');
    } else if (diffInDays < 1) {
      return formatDistanceToNowStrict(date, { addSuffix: true, locale });
    } else if (diffInDays < 7) {
      return `${formatDistanceToNowStrict(date, { addSuffix: true, locale })} ${t(
        'time_in',
      )} ${format(date, 'HH:mm')}`;
    } else if (differenceInYears(now, date) === 0) {
      if (
        (route.name === RESOLVE || route.name === PROFILE) &&
        date.getFullYear() !== now.getFullYear()
      ) {
        return `${format(date, 'dd MMM yyyy', { locale })} ${t('time_in')} ${format(
          date,
          'HH:mm',
        )}`;
      }

      return `${format(date, 'dd MMM', { locale })} ${t('time_in')} ${format(
        date,
        'HH:mm',
      )}`;
    } else {
      return `${format(date, 'dd MMM yyyy', { locale })} ${t('time_in')} ${format(
        date,
        'HH:mm',
      )}`;
    }
  }, [ts, now]);
}

export function useDateShort(ts: number): string {
  const t = useTranslator();
  const now = new Date();
  const lastHourOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
  );
  const date = new Date(ts * 1000);

  const seconds = differenceInSeconds(now, date);
  const minutes = differenceInMinutes(now, date);
  const hours = differenceInHours(now, date);
  const days = differenceInDays(lastHourOfDay, date);

  if (seconds <= 60) {
    return t('time_now');
  } else if (minutes <= 60) {
    return t('time_minutes', { amount: minutes });
  } else if (hours <= 24) {
    return t('time_hours', { amount: hours });
  } else if (days <= 6) {
    return t('time_days', { amount: days });
  } else if (now.getFullYear() - date.getFullYear() < 1) {
    return format(date, 'd MMM').replace(/\./gi, '');
  } else {
    return format(date, 'd MMM yy').replace(/\./gi, '');
  }
}
